import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dialog } from "./Dialog";
import {
    RootState,
    RootStateKind,
    ScanSubStateKind,
    useAnyState
} from "./state/StateProvider";
import * as svg from "./svg";
import { ActionKind } from "../types/Action";

const COUNTDOWN_SECONDS = 5;

const timeoutForState = (state: RootState): number | null => {
    switch (state.kind) {
        case RootStateKind.OutOfOrder:
        case RootStateKind.Welcome:
            return null;
        case RootStateKind.Scan:
            switch (state.subState.kind) {
                case ScanSubStateKind.ProcessPayment:
                    return 100;
                case ScanSubStateKind.CartCheck:
                    return 90;
                default:
                    return 40;
            }
        case RootStateKind.CheckoutSuccess:
            return 15;
        case RootStateKind.Operator:
            return 300;
        case RootStateKind.Flappy:
            return 180;
        case RootStateKind.Error:
            return 10;
        default:
            return state satisfies never;
    }
};

const isModalShownForState = (state: RootState): boolean => {
    switch (state.kind) {
        case RootStateKind.Scan:
            return true;
        default:
            return false;
    }
};

export const Timeout = ({ children }: PropsWithChildren) => {
    const interval = useRef<number>(0);

    const [idleSeconds, setIdleSeconds] = useState(0);

    const { state, dispatch } = useAnyState();

    const timeout = timeoutForState(state);
    const isTimeoutEnabled = timeout != null && !import.meta.env.DEV;
    const remainingSeconds = isTimeoutEnabled && timeout - idleSeconds;
    const isModalShown =
        isModalShownForState(state) &&
        remainingSeconds &&
        remainingSeconds <= COUNTDOWN_SECONDS;

    const reset = () => {
        dispatch({ kind: ActionKind.Reset });
    };

    const resetIdle = (_: Event) => {
        setIdleSeconds(0);
    };

    const resetTimerOnStateChange = () => {
        if (!isTimeoutEnabled) return;

        setIdleSeconds(0);
        interval.current = window.setInterval(() => {
            setIdleSeconds((s) => {
                if (s + 1 >= timeout) {
                    reset();
                }
                return s + 1;
            });
        }, 1000);

        return () => clearInterval(interval.current);
    };

    const resetTimerOnScan = () => {
        if (!isTimeoutEnabled) return;

        window.addEventListener("keypress", resetIdle);

        return () => {
            window.removeEventListener("keypress", resetIdle);
        };
    };

    const resetTimerOnTouch = () => {
        if (!isTimeoutEnabled || isModalShown) return;

        window.addEventListener("pointerdown", resetIdle);

        return () => {
            window.removeEventListener("pointerdown", resetIdle);
        };
    };

    useEffect(resetTimerOnStateChange, [isTimeoutEnabled, state]);
    useEffect(resetTimerOnScan, [isTimeoutEnabled]);
    useEffect(resetTimerOnTouch, [isTimeoutEnabled, isModalShown]);

    return (
        <>
            {isModalShown && (
                <TimeoutModal
                    seconds={remainingSeconds}
                    onContinue={() => setIdleSeconds(0)}
                    onStartOver={reset}
                />
            )}
            {children}
        </>
    );
};

const TimeoutModal = ({
    seconds,
    onContinue,
    onStartOver
}: {
    seconds: number;
    onContinue: () => void;
    onStartOver: () => void;
}) => {
    const { t } = useTranslation();

    return (
        <Dialog
            image={svg.itemLost}
            title={t("stillThere")}
            onBackgroundClick={onContinue}
            primaryButton={{ title: t("continue"), action: onContinue }}
            secondaryButton={{ title: t("startOver"), action: onStartOver }}
        >
            <div className="text-3xl text-primary-800">
                {formatDuration(seconds)}
            </div>
        </Dialog>
    );
};

const formatDuration = (duration: number) => {
    const seconds = duration % 60;
    const minutes = (duration - seconds) / 60;

    return `${padZero(minutes)}:${padZero(seconds)}`;
};

const padZero = (number: number) => number.toString().padStart(2, "0");
